import { Form } from 'antd';
import { Editor } from '@hugerte/hugerte-react';
import { Button, Checkbox, message } from 'antd';
import dompurify from 'dompurify';
import gql from 'graphql-tag';
import { get } from 'lodash';
import { useState, useEffect, useCallback, Fragment } from 'react';
import { withApollo } from 'react-apollo';
import { queryEditEmailsByCompanyId } from 'src/_shared/api/graphql/custom/edit-emails/';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';
import { parse } from 'src/_shared/services/utils.js';
import { COLORS } from '../_shared/styles/colors';
import { JobNotification } from './job-notification';

const sanitizer = dompurify.sanitize;

const EMAIL_TYPES = {
	referred: {
		key: 'referredEmailText',
		title: "You've Been Referred Email",
		description: 'This text will appear below the information about the job.',
		dataPath: '[0].referredEmailText',
	},
	alert: {
		key: 'alertEmailText',
		title: 'New Job Alert Email',
		description: 'This text will appear below the information about the job.',
		dataPath: '[0].alertEmailText',
	},
	invite: {
		key: 'inviteEmailText',
		title: 'New Account Invitation Email',
		description: 'This text will appear above the Accept Invite button.',
		dataPath: '[0].inviteEmailText',
	},
	accepted: {
		key: 'acceptedEmailText',
		title: 'A Referral Has Accepted Email',
		description: 'This text will appear in the referral accepted email.',
		dataPath: '[0].acceptedEmailText',
	},
	application: {
		key: 'applicationEmailText',
		title: 'Your Application Has been Started',
		description:
			'This text will appear in the your application has been started email.',
		dataPath: '[0].applicationEmailText',
	},
	updated: {
		key: 'updatedEmailText',
		title: 'Your Referral Has been Updated Email',
		description:
			'This text will appear in the your referral has been updated email.',
		dataPath: '[0].updatedEmailText',
	},
	ineligible: {
		key: 'ineligibleEmailText',
		title: 'Your Referral is Ineligible',
		description: 'This text will appear in your referral ineligible email.',
		dataPath: '[0].ineligibleEmailText',
	},
	welcome: {
		key: 'welcomeEmailText',
		title: 'Welcome Email',
		description: 'This text will appear in the your welcome email.',
		dataPath: '[0].welcomeEmailText',
	},
	footer: {
		key: 'footerEmailText',
		title: 'Email Footer',
		description: 'This text will appear in your email footer.',
		dataPath: '[0].footerEmailText',
	},
	signature: {
		key: 'signatureEmailText',
		title: 'Email Signature',
		description: 'This text will appear in the your email signature.',
		dataPath: '[0].signatureEmailText',
	},
};

const RTE_CONFIG = {
	height: 220,
	menubar: false,
	branding: false,
	plugins: ['image', 'code', 'media', 'link'],
	toolbar:
		'formatselect |bold italic backcolor | \
     alignleft aligncenter alignright alignjustify | \
     bullist numlist outdent indent  | \
     media link image ',
	automatic_uploads: true,
	file_picker_types: 'image',
};

const EmailEditor = ({
	emailType,
	companyEmailData,
	emailSettings,
	onEditorChange,
	index = null,
}) => {
	const insertContent = useCallback(
		async (inst) => {
			let data = '';

			if (emailType === 'onboarding' && index !== null) {
				const onboardingEmails = get(emailSettings, 'onboarding.inviteEmails');
				data = get(onboardingEmails, `[${index}].bodyText`, '');
			} else if (EMAIL_TYPES[emailType]) {
				data = get(companyEmailData, EMAIL_TYPES[emailType].dataPath, '');
			}

			// Ensure data is always a string and sanitize it
			const sanitizedContent =
				data && typeof data === 'string' ? sanitizer(data) : '';
			inst.setContent(sanitizedContent);
		},
		[emailType, companyEmailData, emailSettings, index]
	);

	const handleChange = useCallback(
		(content, editor) => {
			onEditorChange(emailType, content, index);
		},
		[emailType, onEditorChange, index]
	);

	return (
		<Editor
			init={{
				...RTE_CONFIG,
				init_instance_callback: insertContent,
			}}
			onEditorChange={handleChange}
		/>
	);
};

const EditEmailsComponent = ({
	client,
	currentUser,
	company,
	CompanyEmailData,
	onCreateEditEmails,
	onUpdateEditEmails,
	onUpdateCompany,
}) => {
	const [state, setState] = useState({
		theme: parse(get(currentUser, 'company.theme', '{}')),
		error: false,
		errorMessage: '',
		emailSettings: parse(get(company, 'emailSettings', '{}')),
		emailsText: [],
		emailTexts: {},
		isExisting: false,
		success: false,
		isOptoutReferralUpdates: get(company, 'isOptoutReferralUpdates', false),
		isOptoutChanged: false,
	});

	useEffect(() => {
		setState((prev) => ({
			...prev,
			isOptoutReferralUpdates: get(company, 'isOptoutReferralUpdates', false),
		}));
	}, [company?.isOptoutReferralUpdates]);

	useEffect(() => {
		setState((prev) => ({
			...prev,
			emailSettings: parse(get(company, 'emailSettings', '{}')),
		}));
	}, [company?.emailSettings]);

	const getAllEmailsText = useCallback(
		async (policy = 'network-only', nextToken = null, allEmailsText = []) => {
			try {
				const { data } = await client.query({
					query: gql(queryEditEmailsByCompanyId),
					variables: {
						companyId: get(currentUser, 'companyId'),
						after: nextToken,
					},
					fetchPolicy: policy,
				});
				const result = [...data.queryEditEmailsByCompanyId.items];
				const token = get(data, 'queryEditEmailsByCompanyId.nextToken', null);
				allEmailsText = [...allEmailsText, ...result];

				setState((prev) => ({
					...prev,
					emailsText: allEmailsText,
				}));

				if (token) {
					await getAllEmailsText(policy, token, allEmailsText);
				}
			} catch (error) {
				console.log(error);
			}
		},
		[client, currentUser]
	);

	useEffect(() => {
		if (currentUser?.companyId) {
			getAllEmailsText();
		}
	}, [getAllEmailsText, currentUser?.companyId]);

	const handleEditorChange = useCallback((emailType, content, index = null) => {
		if (emailType === 'onboarding' && index !== null) {
			setState((prev) => {
				const emailSettings = { ...prev.emailSettings };
				const onboardingEmails = get(
					emailSettings,
					'onboarding.inviteEmails',
					[]
				);
				if (onboardingEmails && onboardingEmails[index]) {
					// Create a deep copy to avoid mutating state
					const updatedEmails = onboardingEmails.map((email, i) =>
						i === index ? { ...email, bodyText: content } : email
					);
					emailSettings.onboarding = {
						...emailSettings.onboarding,
						inviteEmails: updatedEmails,
					};
				}
				return {
					...prev,
					emailSettings,
				};
			});
		} else if (EMAIL_TYPES[emailType]) {
			setState((prev) => ({
				...prev,
				emailTexts: {
					...prev.emailTexts,
					[EMAIL_TYPES[emailType].key]: content,
				},
			}));
		}
	}, []);

	const handleError = useCallback((errorMessage = '') => {
		setState((prev) => ({
			...prev,
			error: true,
			success: false,
			errorMessage,
		}));
	}, []);

	const handleReferralUpdates = useCallback(
		(e) => {
			const isChecked = e.target.checked;
			setState((prev) => ({
				...prev,
				isOptoutReferralUpdates: isChecked,
			}));

			if (company?.id) {
				onUpdateCompany({
					input: {
						id: company.id,
						isOptoutReferralUpdates: isChecked,
					},
				});
			}
		},
		[company, onUpdateCompany]
	);

	const handleSubmit = useCallback(
		(e) => {
			e.preventDefault();

			try {
				const emailInput = {
					companyId: get(currentUser, 'companyId'),
					...state.emailTexts,
				};

				if (state.emailsText.length === 0) {
					onCreateEditEmails({ input: emailInput }).then((res) => {
						setState((prev) => ({ ...prev, success: true }));
					});
				} else {
					const id = get(state.emailsText[0], 'id');
					onUpdateEditEmails({
						input: { id, ...emailInput },
					}).then((res) => {
						setState((prev) => ({ ...prev, success: true }));
						message.success('Changes are successfully submitted.');
					});
				}

				if (
					get(state.emailSettings, 'onboarding.inviteEmails', []).length > 0
				) {
					onUpdateCompany({
						input: {
							id: get(currentUser, 'companyId'),
							emailSettings: JSON.stringify(state.emailSettings),
						},
					});
				}
			} catch (error) {
				handleError();
			}
		},
		[
			currentUser,
			state,
			onCreateEditEmails,
			onUpdateEditEmails,
			onUpdateCompany,
			handleError,
		]
	);

	const renderOnboardingEmailEditors = useCallback(() => {
		const onboardingEmails = get(
			state.emailSettings,
			'onboarding.inviteEmails',
			[]
		);
		const htmlEmails = [];
		let subGroup = [];

		onboardingEmails.forEach((email, index) => {
			subGroup.push(email);
			if ((index + 1) % 2 === 0) {
				htmlEmails.push(subGroup);
				subGroup = [];
			} else if (index === onboardingEmails.length - 1) {
				htmlEmails.push(subGroup);
				subGroup = [];
			}
		});

		return htmlEmails.map((subGroup, index) => {
			const subText = 'This text will appear above the Get Started button.';
			return (
				<Fragment key={`onboarding-${index}`}>
					<div className="customizations-box" key={`onboarding-group-${index}`}>
						<Form.Item>
							<h4>
								{`Onboarding Email Invitation #${index + index + 1}`}
								{index > 0 && (
									<span
										style={{
											marginLeft: 10,
											color: COLORS.textSecondary,
											fontWeight: 400,
											fontSize: 16,
										}}
									>{`(Sends after ${get(subGroup, '[0].days')} days)`}</span>
								)}
							</h4>
							<p>{subText}</p>
							<EmailEditor
								emailType="onboarding"
								companyEmailData={CompanyEmailData}
								emailSettings={state.emailSettings}
								onEditorChange={handleEditorChange}
								index={index + index}
							/>
						</Form.Item>
					</div>
					{subGroup.length > 1 && (
						<div
							className="customizations-box"
							key={`onboarding-group-${index}-2`}
						>
							<Form.Item>
								<h4>
									{`Onboarding Email Invitation #${index + index + 2}`}
									<span
										style={{
											marginLeft: 10,
											color: COLORS.textSecondary,
											fontWeight: 400,
											fontSize: 16,
										}}
									>{`(Sends after ${get(subGroup, '[1].days')} days)`}</span>
								</h4>
								<p>{subText}</p>
								<EmailEditor
									emailType="onboarding"
									companyEmailData={CompanyEmailData}
									emailSettings={state.emailSettings}
									onEditorChange={handleEditorChange}
									index={index + index + 1}
								/>
							</Form.Item>
						</div>
					)}
				</Fragment>
			);
		});
	}, [state.emailSettings, CompanyEmailData, handleEditorChange]);

	const renderEmailSection = useCallback(
		(emailType) => {
			const config = EMAIL_TYPES[emailType];
			if (!config) return null;

			return (
				<div className="customizations-box" key={emailType}>
					<Form.Item>
						<h4>{config.title}</h4>
						<p>{config.description}</p>
						<EmailEditor
							emailType={emailType}
							companyEmailData={CompanyEmailData}
							emailSettings={state.emailSettings}
							onEditorChange={handleEditorChange}
						/>
					</Form.Item>
				</div>
			);
		},
		[CompanyEmailData, state.emailSettings, handleEditorChange]
	);

	if (!get(currentUser, 'company') || !company || !currentUser) {
		return <Spinner />;
	}

	return (
		<main>
			<JobNotification />
			<div className="page-title">
				<h2 className="page-heading">Referral Notifications</h2>
			</div>
			<div className="setting-card">
				<Form style={{ marginLeft: 10 }}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Checkbox
							checked={state.isOptoutReferralUpdates}
							style={{ marginRight: 8 }}
							onChange={handleReferralUpdates}
						/>
						<h4
							className="setting-card-title"
							style={{ margin: 0, paddingTop: 1 }}
						>
							Require Employee Opt-In
						</h4>
					</div>
					<p style={{ marginLeft: 24 }}>
						Requires Employees to Opt-In for Referral and Bonus Updates.
					</p>
				</Form>
			</div>
			<div className="page-title">
				<h2 className="page-heading">Email Customizations</h2>
				<ul className="info-action">
					<li>
						<Button
							type="primary"
							size="large"
							className="btn-min-width"
							onClick={handleSubmit}
						>
							Save
						</Button>
					</li>
				</ul>
			</div>
			<section className="msg-notification">
				<Form>
					<div className="mn-customizations">
						{Object.keys(EMAIL_TYPES).map((emailType) =>
							renderEmailSection(emailType)
						)}
						{state.emailSettings?.onboarding?.sendInviteEmails &&
							renderOnboardingEmailEditors()}
					</div>
				</Form>
			</section>
		</main>
	);
};

export default withApollo(EditEmailsComponent);
